// Costs per company, line by line. Customers see their own month under Operations and switch resources on or off;
// founders see every project and set VERANeural's fee, the company's own Azure groups and its outside costs.
// Everything but the fee is passed through at cost, so a company (and its own clients) can decide what to keep on.

import { ScoreCard } from "./ScoreCard";
import { Fragment, useCallback, useEffect, useState } from "react";
import { Head, Pill, useStudio } from "./ui";
import { T } from "../lib/i18n";
import { vera } from "../api/vera";
import { AskVera } from "./AskVera";
import { companyName, costs, money, monthName, outsideText, parseOutside, recentMonths, serviceName, type AllCosts, type CostLine, type CostStatement } from "../lib/costs";

const errorText = (e: unknown) => (e instanceof Error ? e.message : T("VERA's server didn't answer. Please try again."));
const sum = (lines: CostLine[]) => lines.reduce((n, l) => n + l.amount, 0);
const qty = (l: CostLine) => (l.quantity == null ? "" : `${l.quantity.toLocaleString("en-US")} ${T(l.unit ?? "")}`.trim());

function MonthChips({ month, setMonth }: { month: string; setMonth: (m: string) => void }) {
  return <div className="row" style={{ gap: 6 }}>{recentMonths().map((m) => <button key={m} className={`chip ${m === month ? "on" : ""}`} onClick={() => setMonth(m)}>{monthName(m)}</button>)}</div>;
}

function Lines({ lines, currency, empty, detailed }: { lines: CostLine[]; currency: string; empty: string; detailed?: boolean }) {
  if (!lines.length) return <p className="faint">{empty}</p>;
  return (
    <div className="tbl-wrap"><table className="records">
      <thead><tr><th>{T("What")}</th>{detailed && <th>{T("How much")}</th>}<th className="right">{T("Cost")}</th></tr></thead>
      <tbody>{lines.map((l) => (
        <tr key={l.id}>
          <td>{T(l.name)}{l.estimate && <span className="tag quiet">{T("estimate")}</span>}{l.detail && <div className="faint small">{l.detail}</div>}</td>
          {detailed && <td className="faint nowrap num">{qty(l)}</td>}
          <td className="right num nowrap">{money(l.amount, currency)}</td>
        </tr>))}
        <tr><td colSpan={detailed ? 2 : 1}><b>{T("Total")}</b></td><td className="right num nowrap"><b>{money(sum(lines), currency)}</b></td></tr>
      </tbody>
    </table></div>
  );
}

/** One company's month: the totals, its own Azure, VERA's engine, outside services, the fee, and the switches. */
/**
 * VERA's work, always as VERA: the model-by-model token lines (the founders' statement) become one "VERA" line with the
 * tokens and their cost, like the companies see. Every other line is a kind of work she did: how many, how long, cost.
 */
export function veraLines(engine: CostLine[]): CostLine[] {
  const isTokens = (l: CostLine) => l.id.startsWith("in:") || l.id.startsWith("out:");
  const tokens = engine.filter(isTokens);
  if (!tokens.length) return engine;
  const n = tokens.reduce((a, l) => a + (l.quantity ?? 0), 0);
  const answers = engine.filter((l) => l.id === "answers" || l.id === "staff_answers").reduce((a, l) => a + (l.quantity ?? 0), 0);
  const vera: CostLine = { id: "vera", name: "VERA", amount: Math.round(tokens.reduce((a, l) => a + l.amount, 0) * 100) / 100, quantity: answers || null, unit: answers ? "answers" : null,
    estimate: tokens.some((l) => l.estimate), detail: `${n.toLocaleString()} tokens · everything VERA thinks, reads and writes, together` };
  // Like the companies' statement: no rates or price sources on the other lines either (they name the providers).
  return [vera, ...engine.filter((l) => !isTokens(l) && l.id !== "answers" && l.id !== "staff_answers").map((l) => ({ ...l, rate: null, detail: l.amount > 0 ? null : "Included in VERA above" }))];
}

export function StatementView({ s, canSwitch, onSwitch }: { s: CostStatement; canSwitch: boolean; onSwitch: (id: string, on: boolean) => Promise<void> }) {
  const [busy, setBusy] = useState("");
  const work = veraLines(s.engine);
  const flip = async (id: string, on: boolean) => { setBusy(id); try { await onSwitch(id, on); } finally { setBusy(""); } };
  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="grid g3">
        <div className="stat"><span className="faint">{T("Total for")} {monthName(s.month)}</span><b className="num">{money(s.total, s.currency)}</b><small className="faint">{T("so far this month")}</small></div>
        <div className="stat"><span className="faint">{T("Passed through at cost")}</span><b className="num">{money(s.passThrough, s.currency)}</b><small className="faint">{T("Azure, VERA's engine and outside services")}</small></div>
        <div className="stat"><span className="faint">{T(s.fee.name)}</span><b className="num">{money(s.fee.amount, s.currency)}</b><small className="faint">{T(s.fee.detail ?? "")}</small></div>
      </div>
      <div className="split">
        <section className="card stack">
          <h2>{T("Your Azure resources")}</h2>
          {s.azureNote && <p className="faint">{s.azureNote}</p>}
          {!s.azureNote && <Lines lines={s.azure.map((l) => ({ ...l, name: T(serviceName(l.name)) }))} currency={s.currency} empty={T("Nothing charged yet this month.")} />}
        </section>
        <section className="card stack">
          <h2>{T("Outside services")}</h2>
          <Lines lines={s.outside} currency={s.currency} empty={T("None. Services like Meta or phone numbers show here when VERANeural pays them for you.")} />
        </section>
      </div>
      <section className="card stack ops-vera">
        <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}><h2>{T("What VERA did this month")}</h2><b className="num">{money(work.reduce((a, l) => a + l.amount, 0), s.currency)}</b></div>
        <p className="faint small">{T("Every kind of work VERA did for you: how many times or how long, the tokens, and what it cost. Counted as it happens; lines marked estimate use an average per use.")}</p>
        <Lines lines={work} currency={s.currency} empty={T("Nothing used yet this month.")} detailed />
      </section>
      <section className="card stack">
        <h2>{T("Resources")}</h2>
        <p className="faint small">{canSwitch ? T("Switch off what you don't need: VERA stops using it at once, and it stops costing anything.") : T("Your company's owner switches these on or off.")} {T("VERA's answers always stay on.")}</p>
        <div className="list">{s.switches.map((x) => (
          <div className="li" key={x.id}>
            <div className="grow"><b>{T(x.name)}</b><div className="faint small">{T(x.what)}</div></div>
            <Pill tone={x.on ? "ok" : "warn"}>{x.on ? T("On") : T("Off")}</Pill>
            <button className={`toggle ${x.on ? "on" : ""}`} role="switch" aria-checked={x.on} aria-label={T(x.name)} disabled={!canSwitch || busy === x.id} onClick={() => void flip(x.id, !x.on)} />
          </div>))}</div>
      </section>
      <span className="live">{T("Live from Azure and VERA's meters · updated")} {new Date(s.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
    </div>
  );
}

/** A customer's Operations: what the company costs, and what it keeps on. */
export function CompanyOperations() {
  const { getToken, toast } = useStudio();
  const [month, setMonth] = useState(recentMonths()[0]);
  const [data, setData] = useState<{ statement: CostStatement; canChange: boolean } | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => { try { setData(await costs.mine(getToken, month)); setError(""); } catch (e) { setError(errorText(e)); } }, [getToken, month]);
  useEffect(() => { if (vera.mode === "live") void load(); }, [load]);
  if (vera.mode !== "live") return <><Head title={T("Operations")} /><p className="mute">{T("Costs appear here with live VERA.")}</p></>;
  const onSwitch = async (id: string, on: boolean) => {
    try { await costs.setSwitch(getToken, id, on); toast(on ? T("Switched on.") : T("Switched off.")); await load(); } catch (e) { setError(errorText(e)); }
  };
  return (
    <>
      <Head title={T("Operations")} lead={T("What your company costs this month, line by line: what VERA did, the resources you use and what each is charged. Everything except VERANeural's fee is passed through at cost.")}>
        <AskVera heading={T("Operations")} where={`Operations: the company's costs for ${month} (what VERA did, its Azure resources, outside services, the fee, and what can be switched off)`}
          ideas={[T("Why did this month cost what it did?"), T("What could we switch off to save?"), T("What did VERA do most this month?")]} />
        <MonthChips month={month} setMonth={setMonth} />
      </Head>
      <ScoreCard />
      {error && <p className="err">{error}</p>}
      {!data ? !error && <p className="faint">{T("Loading…")}</p> : <StatementView s={data.statement} canSwitch={data.canChange} onSwitch={onSwitch} />}
    </>
  );
}

/** Founders: the fee, the company's own Azure groups and its outside costs. */
function TermsForm({ s, onSaved }: { s: CostStatement; onSaved: () => void }) {
  const { getToken, toast } = useStudio();
  const [fee, setFee] = useState(String(s.fee.amount));
  const [groups, setGroups] = useState(s.resourceGroups.join(", "));
  const [outside, setOutside] = useState(outsideText(s.outside));
  const [error, setError] = useState("");
  const save = async () => {
    try {
      await costs.setTerms(getToken, s.company, {
        monthlyFee: Number(fee.replace(/[^0-9.]/g, "")) || 0,
        resourceGroups: groups.split(/[,\s]+/).map((g) => g.trim()).filter(Boolean),
        outside: parseOutside(outside),
      });
      toast(T("Saved.")); onSaved();
    } catch (e) { setError(errorText(e)); }
  };
  return (
    <section className="card stack">
      <h2>{T("Terms for")}{" "}{companyName(s.company)}</h2>
      <label className="stack" style={{ gap: 4 }}><span className="faint small">{T("VERANeural monthly fee (USD)")}</span><input className="input" value={fee} onChange={(e) => setFee(e.target.value)} inputMode="decimal" /></label>
      <label className="stack" style={{ gap: 4 }}><span className="faint small">{T("The company's own Azure resource groups, comma separated")}</span><input className="input" value={groups} placeholder={T("e.g. vera-marine")} onChange={(e) => setGroups(e.target.value)} /></label>
      <label className="stack" style={{ gap: 4 }}><span className="faint small">{T("Outside services, one per line: name | monthly amount | note")}</span><textarea className="input" rows={3} value={outside} placeholder={T("Meta developer account | 99 | glasses")} onChange={(e) => setOutside(e.target.value)} /></label>
      {error && <p className="err">{error}</p>}
      <div className="row"><button className="btn primary" onClick={() => void save()}>{T("Save terms")}</button></div>
    </section>
  );
}

/** Founders' Operations: every project's month side by side, and the Azure groups that are VERANeural's own. */
export function CostsByProject() {
  const { getToken, toast } = useStudio();
  const [month, setMonth] = useState(recentMonths()[0]);
  const [all, setAll] = useState<AllCosts | null>(null);
  const [open, setOpen] = useState("");
  const [error, setError] = useState("");
  const load = useCallback(async () => { try { setAll(await costs.all(getToken, month)); setError(""); } catch (e) { setError(errorText(e)); } }, [getToken, month]);
  useEffect(() => { if (vera.mode === "live") void load(); }, [load]);
  if (vera.mode !== "live") return null;
  const cur = all?.currency ?? "USD";
  const shown = all?.companies.find((c) => c.company === open);
  const ours = sum(all?.unassigned ?? []);
  return (
    <section className="card stack" style={{ gap: 12 }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2>{T("Costs per project")}</h2>
        <MonthChips month={month} setMonth={setMonth} />
      </div>
      <p className="faint small">{T("Each company pays its own Azure resources, its use of VERA's engine and its outside services at cost, plus VERANeural's fee. Open a project to see what the company sees and set its terms.")}</p>
      {error && <p className="err">{error}</p>}
      {all?.azureNote && <p className="faint">{all.azureNote}</p>}
      {!all ? !error && <p className="faint">{T("Loading…")}</p> : (
        <div className="tbl-wrap"><table>
          <thead><tr><th>{T("Project")}</th><th className="right">{T("Own Azure")}</th><th className="right">{T("VERA's engine")}</th><th className="right">{T("Outside")}</th><th className="right">{T("VERANeural fee")}</th><th className="right">{T("Company pays")}</th></tr></thead>
          <tbody>
            {all.companies.map((c) => (
              <Fragment key={c.company}>
                <tr className="linkish" onClick={() => setOpen(open === c.company ? "" : c.company)} style={{ cursor: "pointer" }}>
                  <td><b>{companyName(c.company)}</b></td>
                  <td className="right num">{c.azureNote ? "n/a" : money(sum(c.azure), cur)}</td>
                  <td className="right num">{money(sum(c.engine), cur)}</td>
                  <td className="right num">{money(sum(c.outside), cur)}</td>
                  <td className="right num">{c.company === "veraneural" ? "" : money(c.fee.amount, cur)}</td>
                  <td className="right num"><b>{c.company === "veraneural" ? "" : money(c.total, cur)}</b></td>
                </tr>
              </Fragment>))}
            {!!all.unassigned.length && <tr><td colSpan={5}><b>{T("VERANeural's own Azure")}</b><div className="faint small">{T("Groups no company owns:")}{" "}{all.unassigned.map((u) => u.name).join(", ")}</div></td><td className="right num"><b>{money(ours, cur)}</b></td></tr>}
          </tbody>
        </table></div>)}
      {!!all?.unassigned.length && open === "" && (
        <div className="hbars">{all.unassigned.map((u) => (
          <div className="hbar" key={u.id}><span title={u.detail ?? ""}>{u.name}</span><div className="track"><span className="fill" style={{ width: `${(u.amount / (all.unassigned[0].amount || 1)) * 100}%` }} /></div><span className="v num">{money(u.amount, cur)}</span></div>))}</div>
      )}
      {shown && (
        <div className="stack" style={{ gap: 12 }}>
          <h2 style={{ marginTop: 6 }}>{companyName(shown.company)}{" "}{T("· what they see")}</h2>
          <StatementView s={shown} canSwitch={shown.company !== "veraneural"} onSwitch={async (id, on) => {
            try { await costs.setSwitchFor(getToken, shown.company, id, on); toast(on ? T("Switched on.") : T("Switched off.")); await load(); } catch (e) { setError(errorText(e)); }
          }} />
          {shown.company !== "veraneural" && <TermsForm key={shown.company} s={shown} onSaved={() => void load()} />}
        </div>
      )}
    </section>
  );
}
